import React, { useState, useRef, useEffect } from "react";
import styles from "./answerContainer.module.scss";
import { Spinner } from "components";
import { VideoPauseIcon, VideoPlayIcon } from "../icons/VideoPlayIcon";
import { useAudio } from "hooks/services/AudioContext";
import classNames from "classnames";

interface IFileLinkProps {
  filePath: string;
  keyValue?: number;
  setVideoPlayer: React.Dispatch<React.SetStateAction<boolean>>;
  messageId: number;
  combinedFile?: boolean;
  threads?: boolean;
}

export const RenderVideo: React.FC<IFileLinkProps> = ({ filePath, keyValue, setVideoPlayer, messageId, threads }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [localIsPlaying, setLocalIsPlaying] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  
  const { 
    stopAudio, 
    playVideo, 
    setCurrentMessageId,
    setIsAudioLoading,
    isVideoIdPlaying
  } = useAudio();

  // Check if this specific video is playing
  const isThisVideoPlaying = isVideoIdPlaying(messageId) || localIsPlaying;

  const handlePlayPause = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (videoElement.paused) {
      // This video is about to play
      setLocalIsPlaying(true);
      stopAudio();
      setCurrentMessageId(null);
      setIsAudioLoading(true);

      // Play this video
      playVideo(videoElement, messageId);
      setShowPauseIcon(true);
      hidePauseIconAfterDelay();
    } else {
      // This video is about to pause
      videoElement.pause();
      setLocalIsPlaying(false);
      setShowPauseIcon(false);
    }
  };

  const handleMouseMove = () => {
    if (isThisVideoPlaying) {
      setShowPauseIcon(true);
      hidePauseIconAfterDelay();
    }
  };

  const hidePauseIconAfterDelay = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      setShowPauseIcon(false);
    }, 1000);
  };

  const handleLoadedData = () => {
    setIsLoaded(true);
  };

  const handleVideoError = () => {
    setVideoPlayer(false);
    setIsLoaded(false);
    setLocalIsPlaying(false);
  };

  // Monitor videoElement.paused to stay synced with actual video state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const checkPlaybackState = () => {
      setLocalIsPlaying(!video.paused);
    };

    const handlePlay = () => {
      setLocalIsPlaying(true);
      setShowPauseIcon(true);
      hidePauseIconAfterDelay();
    };

    const handlePause = () => {
      setLocalIsPlaying(false);
      setShowPauseIcon(false);
    };

    const handleEnded = () => {
      setLocalIsPlaying(false);
      setShowPauseIcon(false);
    };

    // Add event listeners
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    
    // Initial check
    checkPlaybackState();

    // Check periodically to make sure we're in sync
    const intervalId = setInterval(checkPlaybackState, 500);

    // Clean up
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      clearInterval(intervalId);
    };
  }, []);
  return (
    <div
      className={styles.videoContainer}
      style={{
        position: "relative",
        maxHeight: "6rem",
      }}
      onMouseMove={handleMouseMove}
    >
      {!isLoaded && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Spinner extraSmall delFile />
        </div>
      )}

      <video
        key={keyValue}
        ref={videoRef}
        controls={false}
        className="rounded-lg"
        style={{
          maxHeight: "6rem",
        }}
        onLoadedData={handleLoadedData}
        onError={handleVideoError}
        onClick={() => {
          handlePlayPause();
        }}
      >
        <source src={filePath} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Show play icon when video is loaded and not playing */}
      {isLoaded && !localIsPlaying && (
        <div
          className={classNames(styles.playIcon, {
            [styles.threads]: threads,
          })}
          onClick={handlePlayPause}
        >
          <VideoPlayIcon />
        </div>
      )}

      {/* Show pause icon when this video is playing and user interacted recently */}
      {localIsPlaying && showPauseIcon && (
        <div
          className={classNames(styles.playIcon, {
            [styles.threads]: threads,
          })}
          onClick={handlePlayPause}
        >
          <VideoPauseIcon />
        </div>
      )}
    </div>
  );
};